<template>
  <footer class="bg-indigo-950 dark:bg-dark-bg text-neutral-300 pt-16 pb-8 transition-colors duration-300">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
        
        <!-- Company Info -->
        <div class="col-span-1 lg:col-span-1">
          <h3 class="text-white text-2xl font-bold font-heading mb-4">Miracle Cloud</h3>
          <p class="text-sm leading-relaxed mb-6">
            {{ t('hero.subtitle') }}
          </p>
          <div class="flex space-x-3">
            <a href="#" class="w-10 h-10 rounded-full bg-indigo-900 dark:bg-dark-surface/50 flex items-center justify-center hover:bg-accent hover:text-white transition-all duration-300">
              <Facebook class="w-5 h-5" />
              <span class="sr-only">Facebook</span>
            </a>
            <a href="#" class="w-10 h-10 rounded-full bg-indigo-900 dark:bg-dark-surface/50 flex items-center justify-center hover:bg-accent hover:text-white transition-all duration-300">
              <Linkedin class="w-5 h-5" />
              <span class="sr-only">LinkedIn</span>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="col-span-1">
          <h4 class="text-white text-lg font-semibold font-heading mb-6 relative inline-block">
            {{ t('nav.home') }}
            <span class="absolute -bottom-2 left-0 w-8 h-0.5 bg-accent"></span>
          </h4>
          <ul class="space-y-3">
            <li v-for="(link, index) in links" :key="index">
              <template v-if="link.href === '/privacy-policy'">
                <router-link :to="link.href" class="text-sm hover:text-accent transition-colors flex items-center group">
                  <span class="text-accent opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all mr-2">▸</span>
                  {{ link.label }}
                </router-link>
              </template>
              <template v-else>
                <a :href="link.href" class="text-sm hover:text-accent transition-colors flex items-center group">
                  <span class="text-accent opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all mr-2">▸</span>
                  {{ link.label }}
                </a>
              </template>
            </li>
          </ul>
        </div>

        <!-- Services -->
        <div class="col-span-1">
          <h4 class="text-white text-lg font-semibold font-heading mb-6 relative inline-block">
            {{ t('services.title') }}
            <span class="absolute -bottom-2 left-0 w-8 h-0.5 bg-accent"></span>
          </h4>
          <ul class="space-y-3">
            <li v-for="(service, index) in services" :key="index">
              <a href="/#services" class="text-sm hover:text-accent transition-colors flex items-center group">
                <span class="text-accent opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all mr-2">▸</span>
                {{ service.title }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div class="col-span-1">
          <h4 class="text-white text-lg font-semibold font-heading mb-6 relative inline-block">
            {{ t('contact.title') }}
            <span class="absolute -bottom-2 left-0 w-8 h-0.5 bg-accent"></span>
          </h4>
          <ul class="space-y-4">
            <li class="flex items-start">
              <MapPin class="w-5 h-5 mr-3 text-accent shrink-0 mt-0.5" />
              <span class="text-sm leading-relaxed">{{ t('contact.address') }}</span>
            </li>
            <li class="flex items-center">
              <Mail class="w-5 h-5 mr-3 text-accent shrink-0" />
              <a href="mailto:info@miraclecloudtw.com" class="text-sm hover:text-accent transition-colors">info@miraclecloudtw.com</a>
            </li>
          </ul>
        </div>

      </div>

      <!-- Copyright -->
      <div class="border-t border-indigo-900 dark:border-dark-surface pt-8 mt-8 flex flex-col md:flex-row justify-between items-center text-sm transition-colors duration-300">
          <p class="mb-4 md:mb-0 transition-colors duration-300">
           &copy; {{ currentYear }} <strong>Miracle Cloud</strong>. {{ t('footer.allRightsReserved') }}
         </p>
      </div>
    </div>

    <!-- Back to Top Button -->
    <button 
      v-show="showBackToTop" 
      @click="scrollToTop"
      class="fixed bottom-8 right-8 w-12 h-12 bg-accent hover:bg-accent-dark text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transform hover:-translate-y-1"
      aria-label="Back to top"
    >
      <ArrowUp class="w-6 h-6" />
    </button>
  </footer>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowUp, Mail, MapPin, Facebook, Linkedin } from 'lucide-vue-next'

const { t, tm } = useI18n({ useScope: 'global' })
const currentYear = ref(new Date().getFullYear())
const showBackToTop = ref(false)

const links = computed(() => {
  const footerLinks = tm('footer.links')
  if (Array.isArray(footerLinks)) {
    return footerLinks
  }
  return [
    { label: t('nav.home'), href: '/#' },
    { label: t('nav.whyUs'), href: '/#about' },
    { label: t('nav.services'), href: '/#services' },
    { label: t('nav.privacyPolicy'), href: '/privacy-policy' }
  ]
})

const services = computed(() => {
  const rawItems = tm('services.items')
  if (Array.isArray(rawItems)) {
    return rawItems.slice(0, 6)
  }
  return []
})

const handleScroll = () => {
  showBackToTop.value = window.scrollY > 300
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>
